<template>
<v-card class="px-3 pb-3 pt-3 OrderHistoryDetail">
  <v-card-text class="px-0 py-0 pb-0 pt-0">
    <div style="text-align: start" v-if="order.orderType === 'DELIVERY'">
      <div class="subheading font-weight-bold">
        <v-icon>home</v-icon>
        {{ $vuetify.t('$vuetify.account.deliveryDetails') }}
      </div>

      <div class="indentation">
        {{ !!address.unit ? address.unit+' -' : null }} {{ address.addressLine1 }}, {{ address.city }}, {{ address.province }}, {{ address.country }}
      </div>

      <div v-if="!!address.message" class="indentation">
        {{ $vuetify.t('$vuetify.common.message') }}:
        <span class="grey--text">"{{ address.message }}"</span>
      </div>
    </div>
    <div style="text-align: start" v-else-if="order.orderType === 'PICKUP'">
      {{ $vuetify.t('$vuetify.cart.pickup') }}
    </div>
    <div style="text-align: start" v-else-if="order.orderType === 'DINE_IN'">
      {{ $vuetify.t('$vuetify.cart.dinein') }}
    </div>

    <v-list dense>
      <div v-for="orderItem in orderItems" :key="`order_item_history_detail${orderItem.uuid}`" style="margin-bottom: 10px;">
        <order-history-detail-row :orderItem="orderItem" />

        <div :key="`order_item_history_detail_orderItem${orderItem.uuid}`">
          <v-list dense>
            <div v-for="relationItem in orderItem.childItemList" :key="`order_item_history_detail${relationItem.uuid}`">
              <template v-if="relationItem.childItemList && relationItem.childItemList.length">
                <b style="display: block; margin-top: 15px; text-align: left;"> {{ relationItem.name }} </b>

                <order-history-detail-row v-for="child of relationItem.childItemList"
                  :orderItem="child"
                  :key="`order_item_history_detail_child_${child.code}`"
                />
              </template>
            </div>
          </v-list>
        </div>
    </div>
    </v-list>

    <br v-if="redemptionItems.length">
    <reward-options-card v-for="rewardOption in redemptionItems"
      :key="rewardOption.id"
      :reward-option="rewardOption"
      history
      :allowConfig="false"
    />

    <br v-if="punchCards.length">
    <punch-card v-for="punchCard in punchCards"
      :key="punchCard.id"
      :punchCard="punchCard"
      history
    />

    <br v-if="coupons.length">
    <coupon-card v-for="coupon in coupons"
      :key="coupon.code"
      :coupon="coupon"
      showItems
    />

    <v-card flat class="ma-0 pa-0">
      <v-card-text v-if="!!order.orderNotes" class="text-xs-left pt-0">
        <span class="font-weight-bold"> Order Notes: </span> {{ order.orderNotes }}
      </v-card-text>

      <v-card-text class="text-xs-right pa-0" style="font-weight: 600;">
        <hr/>
        <div>
          {{ $vuetify.t('$vuetify.cart.subtotal') }}: {{ order.subTotal | currency }}
        </div>

        <div v-if="order.discountTotal !== 0">{{ $vuetify.t('$vuetify.cart.discount') }}:
          <span style="color: red">{{ order.discountTotal | currency }}</span>
        </div>

        <div v-if="!!order.deliveryCharge">
          {{ $vuetify.t('$vuetify.cart.deliveryFee') }}: {{ order.deliveryCharge | currency }}
        </div>

        <div v-if="!!order.serviceCharges && order.serviceCharges.length && 'amount' in order.serviceCharges[0]">
          {{ $vuetify.t('$vuetify.cart.serviceFee') }}: {{ order.serviceCharges[0].amount | currency }}
        </div>

        <div>
          {{ $vuetify.t('$vuetify.cart.tax') }}: {{ order.taxTotal | currency }}
        </div>

        <div>
          {{ $vuetify.t('$vuetify.cart.tip') }}: {{ order.tip | currency }}
        </div>

        <!-- <v-divider class="my-2" /> -->

        <div>
          {{ $vuetify.t('$vuetify.cart.total') }} ({{ order.currency }}): {{ order.totalAmount | currency }}
        </div>
      </v-card-text>
      <div class="text-xs-left">
        <hr style="margin-top: 5px"/>
        <v-btn class='reorder-button' color="primary" @click="handleOrderItem">
          Reorder
        </v-btn>
      </div>
    </v-card>
  </v-card-text>
</v-card>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import get from 'lodash/get';
  import cloneDeep from 'lodash/cloneDeep';

  import PunchCard from "@/components/Common/PunchCard"
  import CouponCard from '@/components/Coupons/CouponCard'
  import RewardOptionsCard from "@/components/RewardsOptions/RewardOptionsCard"

  import OrderHistoryDetailRow from './OrderHistoryDetailRow'

  export default {
    components: {
      PunchCard,
      CouponCard,
      RewardOptionsCard,
      OrderHistoryDetailRow
    },

    props: {
      orderId: {
        type: String,
        default: ''
      },
      orderObj: {
        type: Object,
        default: null
      }
    },

    computed: {
      order() {
        return this.orderObj || this.getOrder(this.orderId) || {}
      },
      orderItems() {
        return get(this.order, 'subOrderList[0].orderItemList', [])
      },

      redemptionItems() {
        return get(this.order, 'subOrderList[0].redemptionItems', [])
      },
      coupons() {
        return get(this.order, 'subOrderList[0].coupons', [])
      },
      punchCards() {
        return get(this.order, 'subOrderList[0].punchCards', [])
      },

      address() {
        return {
          addressLine1: get(this.order, 'deliveryAddress.addressLine1', ''),
          addressLine: get(this.order, 'deliveryAddress.addressLine2', ''),
          city: get(this.order, 'deliveryAddress.city', ''),
          postCode: get(this.order, 'deliveryAddress.postCode', ''),
          country: get(this.order, 'deliveryAddress.country', ''),
          province: get(this.order, 'deliveryAddress.province', ''),
          message: get(this.order, 'deliveryMessage', ''),
          unit: get(this.order, 'deliveryAddress.unit', '')
        }
      },

      ...mapGetters([ 'getOrder' ])
    },
    methods: {
      ...mapActions([
        'addManyToCart',
        'showSnack'
      ]),
      handleOrderItem() {
        const itemsToAdd = []
        const removeUuids = (obj) => {
          if (obj && typeof obj === 'object') {
            if (Array.isArray(obj)) {
              return obj.map((item) => removeUuids(item));
            } else {
              const newObj = { ...obj };
              delete newObj.uuid;

              for (const key in newObj) {
                newObj[key] = removeUuids(newObj[key]);
              }

              return newObj;
            }
          } else {
            return obj;
          }
        };
        this.orderItems.map((orderItem) => {
          const _clone = cloneDeep(orderItem)
          const item = { 
            price: _clone.price,
            code: _clone.code,
            nonDiscountable: _clone.nonDiscountable || false,
            name: _clone.name,
            itemAttributeList: _clone.itemAttributeList || [],
            qty: _clone.qty,
            raw: _clone,
            childItemList: _clone.childItemList || []
          }
          itemsToAdd.push(removeUuids(item));
        })
        this.addManyToCart(itemsToAdd)
        this.showSnack({
            text: this.$vuetify.t(`Added ${itemsToAdd.length} items to cart`),
            color: 'secondary'
        })
      }
    }

  }
</script>

<style>
  .OrderHistoryDetail {
    font-size: var(--text-size-1);
  }

  /* #orderNowButton {
    width: 100%;
  } */

  /* #orderNowButton .v-btn {
    font-size: calc(var(--text-size-1) * 1.2);
  } */
</style>
<style scoped>
  .v-card-text {
    padding: 0 !important;
  }
  hr {
    border: none;
    height: 1px;
    background-color: #D9D9D9; 
    margin: 0;
    margin-bottom: 5px !important;
  }
  .reorder-button {
    height: 50px;
    font-size: 14px;
    background-color: var(--v-primary-base)!important;
    color: white !important;
    font-weight: bold;
    width: 48.5%;
    border-radius: 25px;
    text-decoration: none;
  }
</style>
